// src/lib/shopify.js
// Core Shopify Storefront API client.
// Uses the @inContext directive to return prices in the user's local currency
// based on their detected country (managed by CurrencyContext).

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const SHOPIFY_TOKEN = process.env.REACT_APP_SHOPIFY_TOKEN;

// Use the latest stable Storefront API version
// Shopify supports each version for ~12 months from release, then retires it —
// re-check shopify.dev/docs/api/usage/versioning before this goes stale again.
const API_VERSION = '2026-07';

// Warn early in the console if env vars are missing (helps with Netlify debugging)
if (!SHOPIFY_DOMAIN || !SHOPIFY_TOKEN) {
    console.error(
        '[Shopify] Missing environment variables. ' +
        'Ensure REACT_APP_SHOPIFY_DOMAIN and REACT_APP_SHOPIFY_TOKEN are set in Netlify.'
    );
}

const endpoint = `https://${SHOPIFY_DOMAIN}/api/${API_VERSION}/graphql.json`;

/**
 * Makes a GraphQL request to the Shopify Storefront API.
 * @param {string} query - GraphQL query or mutation string
 * @param {object} variables - Variables to pass with the query
 * @returns {object} The data field from the Shopify response
 */
export async function shopifyFetch(query, variables = {}) {
    // Guard: throw a readable error if config is missing
    if (!SHOPIFY_DOMAIN || !SHOPIFY_TOKEN) {
        throw new Error('Store configuration missing. Please refresh the page.');
    }

    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': SHOPIFY_TOKEN,
            },
            body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
            throw new Error(`Shopify API error: ${response.status}`);
        }

        const { data, errors } = await response.json();

        // Surface GraphQL-level errors with a readable message
        if (errors) {
            throw new Error(errors[0].message);
        }

        return data;

    } catch (err) {
        // Catch the cryptic browser URL error that appears when env vars are undefined
        if (
            err.message.includes('string did not match') ||
            err.message.includes('Failed to fetch')
        ) {
            throw new Error('Unable to connect to store. Please refresh the page.');
        }
        throw err;
    }
}